import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import StandardModal, { Actions, PatientName } from 'components/shared/StandardModal';
import { useTourMutation } from 'hooks/useWorkflowTours';
import { parseDate, simpleDashDate } from 'lib/date';
import { TourOptions } from 'models/Tour';
import Workflow, { WorkflowGroupOptions } from 'models/Workflow';
import { createTour, updateTour } from 'services/api/tours';

type Props = {
  workflow: Workflow;
  onClose: () => void;
} & (
  | { tour: TourOptions; facilityName: string; options?: never }
  // The groups in view that have no tour yet.
  | { tour?: never; facilityName?: never; options: WorkflowGroupOptions[] }
);

export default function TourModal({ workflow, tour, facilityName, options, onClose }: Props) {
  const editing = !!tour;
  // Editing never moves a tour to another facility (that is delete + log), so the select is shown disabled.
  const facilities = tour ? [{ id: tour.groupId, name: facilityName }] : options;
  const [groupId, setGroupId] = useState(tour?.groupId ?? '');
  const [touredOn, setTouredOn] = useState<Date | null>(tour ? parseDate(tour.touredOn) : new Date());

  const log = useTourMutation({
    workflowId: workflow.id,
    mutationFn: createTour,
    onSuccess: onClose,
  });
  const update = useTourMutation({
    workflowId: workflow.id,
    mutationFn: updateTour,
    onSuccess: onClose,
  });
  const isPending = log.isPending || update.isPending;

  const dateChanged = !tour || simpleDashDate(touredOn) !== tour.touredOn;
  const canSubmit = facilities.some((group) => group.id === groupId) && !!touredOn && dateChanged;

  const submit = () => {
    if (!canSubmit) return;
    const date = simpleDashDate(touredOn)!;
    if (tour) update.mutate({ workflowId: workflow.id, id: tour.id, touredOn: date });
    else log.mutate({ workflowId: workflow.id, groupId, touredOn: date });
  };

  return (
    <StandardModal title={editing ? 'Edit Tour Log' : 'Log Tour'} onCancel={onClose}>
      <PatientName>Patient: {workflow.patientName}</PatientName>
      <Form data-testid='tour-modal'>
        <div>
          <FieldLabel>Facility</FieldLabel>
          <Select<WorkflowGroupOptions>
            options={facilities}
            value={facilities.find((group) => group.id === groupId) ?? null}
            onChange={(group) => setGroupId(group?.id ?? '')}
            getOptionValue={(group) => group.id}
            getOptionLabel={(group) => group.name}
            placeholder='Facility'
            disabled={editing}
            dataTestId='tour-facility-select'
          />
        </div>
        <div>
          <FieldLabel>Tour Date</FieldLabel>
          {/* react-datepicker drops typed text that is partial, impossible or past maxDate without calling
              onChange, which would leave the previous date to be saved, so typing clears the date until onChange
              accepts one. Calendar clicks also reach onChangeRaw but must not clear: re-picking the held day
              fires no onChange. */}
          <DatePicker
            selected={touredOn}
            maxDate={new Date()}
            onChangeRaw={(event) => event?.target instanceof HTMLInputElement && setTouredOn(null)}
            onChange={setTouredOn}
          />
        </div>
      </Form>
      <Actions>
        <Button variant='ghost' onClick={onClose} data-testid='tour-cancel'>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!canSubmit || isPending} loading={isPending} data-testid='tour-submit'>
          {editing ? 'Update' : 'Submit'}
        </Button>
      </Actions>
    </StandardModal>
  );
}

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
`;
